import { isAxiosError } from 'axios'
import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IRadioBoxProps } from 'shared/ui/Radio'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type RowUser } from 'entities/Users/model/Table/RowUser'
import { EnumUserRoles } from 'entities/Users/model/types'
import { UsersApi } from 'entities/Users'
import { type IResponseDeleteMemberError } from 'entities/Users/api/types'
import { DeleteMemberModal } from 'pages/settings/pages/members/ui/actions/deleteMember/ui/DeleteMemberModal/DeleteMemberModal'

type SeatOption = 'keep' | 'remove'

export class DeleteMemberStore {
  private _modalId = nanoid()
  private _deleteSeatOption: SeatOption = 'keep'
  private _deleteConfirmCheckbox = false
  private _reassignContactsUserId: number | null = null
  private _reassignConversationsUserId: number | null = null
  private _reassignInboxOwnershipUserId: number | null = null
  private _disposeReaction: IReactionDisposer | null = null

  constructor(
    private _memberToDelete: RowUser,
    private _entities: IEntitiesStore,
    private _onSuccess?: () => void | Promise<void>
  ) {
    makeAutoObservable(this)
    this.openDeleteMemberModal()
  }

  openDeleteMemberModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Delete member',
      type: ModalTypeList.DEFAULT,
      ModalContent: DeleteMemberModal,
      ModalContentProps: {
        store: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onCloseDeleteMemberModal,
      primaryAction: {
        text: 'Delete member',
        disabled: this.deleteIsDisabled,
        onAction: this.onDeleteMember,
      },
    })

    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => this.deleteIsDisabled,
      (isDisabled) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = isDisabled
        }
      }
    )
  }

  onCloseDeleteMemberModal = () => {
    this._disposeReaction?.()
    modalStore.removeModal(this._modalId)
  }

  onSetSeatOption = (option: SeatOption) => {
    this._deleteSeatOption = option
  }

  onSetDeleteConfirmCheckbox = (value: boolean) => {
    this._deleteConfirmCheckbox = value
  }

  onSetReassignContactsUserId = (userId: number) => {
    this._reassignContactsUserId = userId
  }

  onSetReassignConversationsUserId = (userId: number) => {
    this._reassignConversationsUserId = userId
  }

  onSetReassignInboxOwnershipUserId = (userId: number) => {
    this._reassignInboxOwnershipUserId = userId
  }

  onDeleteMember = async () => {
    if (!this._memberToDelete) return

    try {
      await UsersApi.deleteMember(this._memberToDelete.id, {
        confirm: this._deleteConfirmCheckbox,
        reassign_contacts_user_id: this._reassignContactsUserId ?? undefined,
        reassign_conversations_user_id: this._reassignConversationsUserId ?? undefined,
        reassign_teams_user_id: this._reassignInboxOwnershipUserId ?? undefined,
        remove_seat: Number(this.isRemoveSeat),
      })

      toastStore.add({
        type: 'success',
        title: `${this._memberToDelete?.fullName} removed from workspace`,
      })
      this._onSuccess?.()

      this._entities.usersStore.removeItem(this._memberToDelete.id)
      this._entities.subscriptionStore.updateSeats()
      this.onCloseDeleteMemberModal()
    } catch (error) {
      logger.error(error)
      let errorMessage = 'Failed to delete member. Please try again'
      if (isAxiosError<IResponseDeleteMemberError>(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      toastStore.add({
        type: 'error',
        title: errorMessage,
      })
    }
  }

  get availableMembersForReassignment() {
    return this._entities.usersStore.users.filter(
      (user) => user.id !== this._memberToDelete?.id && user.role !== EnumUserRoles.VIEW_ONLY
    )
  }

  get memberToDelete() {
    return this._memberToDelete
  }

  get hasPaidSeat() {
    return this._memberToDelete?.role !== EnumUserRoles.VIEW_ONLY
  }

  get hasInboxOwnership() {
    return this._memberToDelete?.origin.hasInboxOwnership ?? false
  }

  get isTrial() {
    return this._entities.subscriptionStore.isTrial
  }

  get showSeatOption() {
    return this.hasPaidSeat && !this.isTrial
  }

  get isRemoveSeat(): boolean {
    if (!this.hasPaidSeat) return false
    if (this.isTrial) return true

    return this._deleteSeatOption === 'remove'
  }

  get deleteSeatOption() {
    return this._deleteSeatOption
  }

  get deleteConfirmCheckbox() {
    return this._deleteConfirmCheckbox
  }

  get reassignContactsUserId() {
    return this._reassignContactsUserId
  }

  get reassignConversationsUserId() {
    return this._reassignConversationsUserId
  }

  get reassignInboxOwnershipUserId() {
    return this._reassignInboxOwnershipUserId
  }

  get seatOptions(): IRadioBoxProps<SeatOption>[] {
    return [
      {
        id: 'keep',
        value: 'keep',
        text: 'Keep seat',
      },
      {
        id: 'remove',
        value: 'remove',
        text: 'Remove seat',
      },
    ]
  }

  get deleteIsDisabled() {
    if (!this._deleteConfirmCheckbox) return true

    const needsReassignment = this.hasPaidSeat

    if (needsReassignment) {
      if (!this._reassignContactsUserId) return true
      if (!this._reassignConversationsUserId) return true
      if (this.hasInboxOwnership && !this._reassignInboxOwnershipUserId) {
        return true
      }
    }

    return false
  }
}
